<template>
  <div class="ledger-page pb-5 pb-md-2">
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner />
    </div>
    <template v-else-if="cost">
      <b-row>
        <b-col
          v-for="card in cards"
          :key="card.label"
          cols="6"
          md="3"
          class="mb-1"
        >
          <b-card class="rounded-lg h-100">
            <div class="text-muted small">
              {{ card.label }}
            </div>
            <strong class="ledger-num">{{ card.value }}</strong>
          </b-card>
        </b-col>
      </b-row>
      <b-card class="rounded-lg">
        <div class="d-flex justify-content-between">
          <span>{{ $t('ledger_cost_budget_used') }}</span>
          <span class="ledger-num">{{ percent(cost.budget_utilization) }}</span>
        </div>
        <b-progress
          :value="Number(cost.budget_utilization) || 0"
          :max="100"
          class="mt-50"
        />
        <b-row
          v-if="canEditBudget"
          class="mt-1"
        >
          <b-col
            cols="8"
            md="4"
          >
            <b-form-input
              v-model.number="budget"
              type="number"
              min="0"
              step="0.01"
            />
          </b-col>
          <b-col cols="4">
            <b-button
              variant="primary"
              :disabled="saving"
              @click="saveBudget"
            >
              {{ $t('ledger_assets_save') }}
            </b-button>
          </b-col>
        </b-row>
        <p class="text-muted small mt-1 mb-0">
          {{ $t('ledger_cost_actual_readonly') }}
          <strong class="ledger-num">{{ money(cost.actual_cost) }}</strong>
        </p>
      </b-card>
      <b-row>
        <b-col
          cols="12"
          md="6"
        >
          <b-card class="rounded-lg">
            <h5>{{ $t('ledger_cost_costs') }}</h5>
            <div
              v-for="row in cost.cost_breakdown || []"
              :key="row.code"
              class="d-flex justify-content-between"
            >
              <span>{{ row.code }} {{ accountName(row) }}</span>
              <span class="ledger-num">{{ money(row.amount) }}</span>
            </div>
          </b-card>
        </b-col>
        <b-col
          cols="12"
          md="6"
        >
          <b-card class="rounded-lg">
            <h5>{{ $t('ledger_cost_revenue') }}</h5>
            <div
              v-for="row in cost.revenue_breakdown || []"
              :key="row.code"
              class="d-flex justify-content-between"
            >
              <span>{{ row.code }} {{ accountName(row) }}</span>
              <span class="ledger-num">{{ money(row.amount) }}</span>
            </div>
          </b-card>
        </b-col>
      </b-row>
      <b-card
        v-if="cost.contract"
        class="rounded-lg"
      >
        <div class="d-flex justify-content-between align-items-start flex-wrap">
          <div>
            <h5 class="mb-50">
              {{ cost.contract.contract_number }}
            </h5>
            <b-badge
              v-if="contractEnded"
              variant="dark"
              class="mr-50"
            >
              {{ $t('ledger_ct_ended') }}
            </b-badge>
            <b-badge
              v-if="contractUnearned > 0"
              variant="warning"
              class="mr-50"
            >
              {{ $t('ledger_ct_billed_ahead') }} {{ money(contractUnearned) }}
            </b-badge>
            <b-badge
              v-if="contractUnbilled > 0"
              variant="info"
            >
              {{ $t('ledger_ct_unbilled') }} {{ money(contractUnbilled) }}
            </b-badge>
          </div>
          <b-link
            v-if="hasPermission('view contracts')"
            :to="{ name: 'ledger-contracts-view', params: { id: cost.contract.id } }"
          >
            {{ $t('ledger_ct_show') }}
          </b-link>
        </div>
        <div class="d-flex justify-content-between mt-1">
          <span>{{ $t('ledger_ct_percent') }}</span>
          <span class="ledger-num">{{ fractionPercent(cost.contract.percent_complete) }}</span>
        </div>
        <b-progress
          :value="fractionBar(cost.contract.percent_complete)"
          :max="100"
          class="mt-50"
        />
        <div class="ledger-num mt-1">
          {{ $t('ledger_ct_value') }} {{ money(cost.contract.contract_value) }}
        </div>
        <div class="ledger-num">
          {{ $t('ledger_ct_recognized') }} {{ money(cost.contract.revenue_recognized) }}
        </div>
        <div class="ledger-num">
          {{ $t('ledger_ct_billed') }} {{ money(cost.contract.billed_to_date) }}
        </div>
        <div class="ledger-num">
          {{ $t('ledger_ct_collected') }} {{ money(cost.contract.collected) }}
        </div>
        <div class="ledger-num">
          {{ $t('ledger_ct_retention_held') }} {{ money(cost.contract.retention_held) }}
        </div>
      </b-card>
      <b-card
        v-if="cost.invoices"
        class="rounded-lg"
      >
        <h5>{{ $t('ledger_inv_title') }}</h5>
        <div>{{ $t('ledger_cost_invoiced') }} <span class="ledger-num">{{ money(cost.invoices.invoiced) }}</span></div>
        <div>{{ $t('ledger_cost_collected') }} <span class="ledger-num">{{ money(cost.invoices.collected) }}</span></div>
        <div>{{ $t('ledger_inv_advance_applied') }} <span class="ledger-num">{{ money(cost.invoices.advances_applied) }}</span></div>
        <div>{{ $t('ledger_cost_outstanding') }} <span class="ledger-num">{{ money(cost.invoices.outstanding) }}</span></div>
        <b-link
          v-if="hasPermission('view invoices')"
          :to="{ name: 'ledger-invoices', query: { project_id: projectId } }"
        >
          {{ $t('ledger_inv_title') }}
        </b-link>
      </b-card>
    </template>
    <div
      v-if="cost"
      class="ledger-inv-sticky d-md-none"
    >
      <span>{{ $t('ledger_cost_profit') }}</span>
      <strong class="ledger-num">{{ money(cost.profit) }}</strong>
    </div>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BProgress, BFormInput, BButton, BLink, BSpinner, BBadge,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getProjectCost, updateProjectAccounting } from '@/api/accounting/projectCost'
import { oneFrom, formatLedgerMoney, roundMoney, apiErrorMessage } from '@/utils/ledger'

export default {
  name: 'ProjectCostTab',
  components: {
    BRow, BCol, BCard, BProgress, BFormInput, BButton, BLink, BSpinner, BBadge,
  },
  props: {
    projectId: { type: Number, required: true },
  },
  data() {
    return { loading: false, saving: false, cost: null, budget: null }
  },
  computed: {
    canEditBudget() {
      return this.hasPermission('financial reports')
    },
    contractEnded() {
      return Boolean(this.cost && this.cost.contract && this.cost.contract.ended)
    },
    contractUnearned() {
      const contract = this.cost && this.cost.contract
      if (!contract) return 0
      const value = contract.unearned_revenue != null ? contract.unearned_revenue : contract.billed_ahead
      return Number(value) || 0
    },
    contractUnbilled() {
      const contract = this.cost && this.cost.contract
      if (!contract) return 0
      const value = contract.unbilled_revenue != null ? contract.unbilled_revenue : contract.unbilled_work
      return Number(value) || 0
    },
    cards() {
      if (!this.cost) return []
      return [
        { label: this.$t('ledger_cost_budget'), value: this.money(this.cost.budget) },
        { label: this.$t('ledger_cost_actual'), value: this.money(this.cost.actual_cost) },
        { label: this.$t('ledger_cost_revenue'), value: this.money(this.cost.revenue) },
        { label: this.$t('ledger_cost_profit'), value: `${this.money(this.cost.profit)} (${this.percent(this.cost.margin_percent)})` },
      ]
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    percent(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return `${n.toFixed(2)}%`
    },
    fractionPercent(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return `${(n * 100).toFixed(2)}%`
    },
    fractionBar(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return 0
      return Math.max(0, Math.min(100, n * 100))
    },
    accountName(row) {
      if (this.$i18n.locale === 'en') return row.name_en || row.name || ''
      return row.name || row.name_en || ''
    },
    async load() {
      this.loading = true
      try {
        const res = await getProjectCost(this.projectId)
        this.cost = oneFrom(res)
        this.budget = this.cost && this.cost.budget != null ? Number(this.cost.budget) : null
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: { title: apiErrorMessage(error, this.$t('ledger_error')), icon: 'AlertCircleIcon', variant: 'danger' },
        })
      } finally {
        this.loading = false
      }
    },
    async saveBudget() {
      this.saving = true
      try {
        await updateProjectAccounting(this.projectId, {
          budget: roundMoney(this.budget || 0),
          currency: (this.cost && this.cost.currency) || 'EGP',
        })
        await this.load()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: { title: apiErrorMessage(error, this.$t('ledger_error')), icon: 'AlertCircleIcon', variant: 'danger' },
        })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; }
.ledger-inv-sticky {
  position: fixed; bottom: 0; inset-inline: 0; z-index: 20; display: flex;
  justify-content: space-between; background: #fff; border-top: 1px solid #e6e6e6;
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
}
</style>
